import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAuthContext } from '../../auth/AuthContext';
import { useMembership } from '../../auth/MembershipContext';
import PageTemplate from '../../components/PageTemplate/PageTemplate';
import { useFormConfig } from '../../hooks/useFormConfig';
import {
  type CorrectionJob,
  type ManagedField,
  type ManagedOption,
  managedOptionsRequest,
} from '../../service/managed-options';
import { canManageMembers } from '../../service/membership';
import OptionEditor from './OptionEditor';

export default function ManagedOptionsScreen() {
  const { tenant } = useFormConfig();
  const { me } = useMembership();
  const { fetchJWT } = useAuthContext();
  const allowed = tenant === 'gfie' && canManageMembers(me, tenant);
  const [editing, setEditing] = useState<ManagedOption | 'new' | null>(null);
  const [jobId, setJobId] = useState('');
  const [message, setMessage] = useState('');
  const [selectedField, setSelectedField] = useState('');
  const queryClient = useQueryClient();
  const request = async <T,>(body?: Record<string, unknown>, query = '') => {
    const { idToken } = await fetchJWT();
    if (!idToken) throw new Error('Sign in again to manage form values.');
    return managedOptionsRequest<T>(idToken, tenant || '', body, query);
  };
  const catalog = useQuery({
    queryKey: ['managed-options', tenant],
    enabled: allowed,
    queryFn: () => request<{ fields: ManagedField[] }>(),
  });
  const field = catalog.data?.fields.find((item) => item.field === selectedField);
  const isPathway = field?.field === 'gfie-pathways';
  const currentJob = jobId || field?.runningJob || '';
  const job = useQuery({
    queryKey: ['managed-options-job', tenant, currentJob],
    enabled: allowed && Boolean(currentJob),
    queryFn: () =>
      request<CorrectionJob>(
        undefined,
        `?field=${encodeURIComponent(field!.field)}&job=${encodeURIComponent(currentJob)}`,
      ),
    refetchInterval: (query) =>
      ['counting', 'running'].includes(query.state.data?.status || '') ? 2000 : false,
  });
  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      request<CorrectionJob | { status: string }>({
        field: field!.field,
        revision: field!.revision,
        ...body,
      }),
    onSuccess: (result) => {
      setEditing(null);
      setMessage('Change saved.');
      if ('sk' in result) {
        queryClient.setQueryData(['managed-options-job', tenant, result.sk], result);
        setJobId(result.sk);
        setMessage('');
      }
      void catalog.refetch();
    },
  });
  useEffect(() => {
    if (job.data?.status === 'complete') void catalog.refetch();
  }, [job.data?.status, catalog.refetch]);
  if (!allowed)
    return (
      <PageTemplate title="Manage form values">
        <p className="govuk-body">Tenant admin access is required.</p>
      </PageTemplate>
    );
  const error = catalog.error || mutation.error || job.error;
  const busy =
    mutation.isPending ||
    ['counting', 'running'].includes(job.data?.status || '') ||
    Boolean(field?.runningJob && job.data?.status !== 'complete');
  return (
    <PageTemplate title="Manage form values" fullWidth>
      <p className="govuk-body">
        Manage the values available in this form. Changes apply to this tenant only.
      </p>
      {error && (
        <p role="alert" className="govuk-error-message">
          {error.message}
        </p>
      )}
      {message && (
        <p role="status" className="govuk-body">
          {message}
        </p>
      )}
      {catalog.isLoading && <p role="status">Loading values...</p>}
      {catalog.data?.fields.length === 0 && <p>No configurable fields.</p>}
      {Boolean(catalog.data?.fields.length) && (
        <>
          <div className="govuk-form-group">
            <label className="govuk-label" htmlFor="managed-field">
              Field to edit
            </label>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <select
                className="govuk-select"
                id="managed-field"
                value={selectedField}
                disabled={mutation.isPending}
                onChange={(event) => {
                  setSelectedField(event.target.value);
                  setEditing(null);
                  setJobId('');
                  setMessage('');
                  mutation.reset();
                }}
              >
                <option value="">Please select...</option>
                {catalog.data?.fields.map((item) => (
                  <option key={item.field} value={item.field}>
                    {item.field === 'gfie-pathways' ? 'Pathways' : item.title}
                  </option>
                ))}
              </select>
              {field && field.revision > 0 && !editing && (
                <button
                  type="button"
                  className="govuk-button govuk-!-margin-bottom-0"
                  disabled={busy}
                  onClick={() => setEditing('new')}
                >
                  Edit {isPathway ? 'Pathways' : field.title}
                </button>
              )}
            </div>
          </div>
        </>
      )}
      {field && (
        <>
          {!field.revision ? (
            <p role="alert" className="govuk-error-message">
              Form values are unavailable because the deployment migration has not completed.
              Contact the service team.
            </p>
          ) : (
            <>
              {job.data && (
                <section aria-label="Correction progress" className="govuk-inset-text">
                  {job.data.last_error && (
                    <p role="alert" className="govuk-error-message">
                      {job.data.last_error}
                    </p>
                  )}
                  <p role="status">
                    {job.data.status === 'counting'
                      ? 'Counting affected forms...'
                      : job.data.status === 'running'
                        ? 'Correcting saved answers...'
                        : job.data.status === 'complete'
                          ? `Correction complete. ${job.data.updated_answers} answer records updated.`
                          : 'Review correction'}
                  </p>
                  <p>
                    {job.data.old_value} → {job.data.value}
                  </p>
                  {job.data.status === 'ready' && (
                    <>
                      <p>
                        {job.data.drafts} draft forms; {job.data.submitted} submitted or
                        approval-stage forms; {job.data.legacy || 0} legacy forms.
                      </p>
                      <p>
                        Updates all matching saved answers, including submitted forms. ID and
                        version stay unchanged. Counts may change while forms are being edited.
                        Previously generated exports are not rewritten.
                      </p>
                      <button
                        type="button"
                        className="govuk-button govuk-button--warning"
                        disabled={mutation.isPending}
                        onClick={() =>
                          mutation.mutate({
                            action: 'confirm',
                            job: currentJob,
                            revision: Number(job.data!.revision),
                          })
                        }
                      >
                        Confirm correction of all matching answers
                      </button>
                    </>
                  )}
                  {['counting', 'running'].includes(job.data.status) && (
                    <button
                      type="button"
                      className="govuk-button govuk-button--secondary"
                      disabled={mutation.isPending}
                      onClick={() => mutation.mutate({ action: 'resume', job: currentJob })}
                    >
                      Resume correction if interrupted
                    </button>
                  )}
                  {job.data.status === 'complete' && (
                    <button
                      type="button"
                      className="govuk-button govuk-button--secondary"
                      onClick={() => {
                        setJobId('');
                        void catalog.refetch();
                      }}
                    >
                      Done
                    </button>
                  )}
                </section>
              )}
              {editing && (
                <OptionEditor
                  isPathway={isPathway}
                  key={editing === 'new' ? 'new' : `${editing.id}-${editing.version}`}
                  option={editing === 'new' ? undefined : editing}
                  busy={busy}
                  onSubmit={(body) => mutation.mutate(body)}
                  onCancel={() => setEditing(null)}
                />
              )}
              <div style={{ overflowX: 'auto' }}>
                <table className="govuk-table">
                  <caption className="govuk-table__caption govuk-table__caption--m">
                    Existing values and versions
                  </caption>
                  <thead>
                    <tr>
                      {[
                        'Value',
                        'Version',
                        ...(isPathway ? ['Cross-government default'] : []),
                        'Status',
                        'Action',
                      ].map((label) => (
                        <th className="govuk-table__header" scope="col" key={label}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {field.options.map((option) => (
                      <tr key={`${option.id}-${option.version}`}>
                        <td className="govuk-table__cell">{option.value}</td>
                        <td className="govuk-table__cell">{option.version}</td>
                        {isPathway && (
                          <td className="govuk-table__cell">
                            {option.metadata.crossGovernmentDefault ? 'Yes' : 'No'}
                          </td>
                        )}
                        <td className="govuk-table__cell">
                          {option.retired ? `Retired ${option.retired}` : 'Active'}
                        </td>
                        <td className="govuk-table__cell">
                          <button
                            type="button"
                            className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                            disabled={busy}
                            aria-label={`Edit ${option.value}, version ${option.version}`}
                            onClick={() => setEditing(option)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </PageTemplate>
  );
}
